<template>
  <div class="early-renewal-modal">
    <div class="early-renewal-modal__modal">
      <div class="early-renewal-modal__header">
        <span class="early-renewal-modal__header-text">
          You're saving ${{ getSaving }} by renewing early!
        </span>
        <button
          v-if="showClose"
          class="early-renewal-modal__close"
          @click="$emit('close-modal')"
        >
          <XIcon class="early-renewal-modal__close--icon" />
        </button>
      </div>
      <div class="early-renewal-modal__content-wrapper">
        <div v-if="!loading" class="early-renewal-modal__content">
          <div class="early-renewal-modal__inner-content">
            <div class="early-renewal-modal__left-side">
              <Plan
                :isPromotion="true"
                :plan="currentPlan"
                :hasMonthlyPlan="false"
                :promotionContent="promotionContent"
              />
              <div class="early-renewal-modal__small-print">
                *After the first year the renewal cost will be ${{
                  currentPlanPrice
                }}
                per year.
              </div>
            </div>
            <div class="early-renewal-modal__right-side">
              <div class="early-renewal-modal__title">Change summary</div>
              <div class="early-renewal-modal__text1">
                You're renewing your {{ currentPlan.name }} subscription at a
                special rate of ${{ promotionContent.price_usd }} (regularly ${{
                  currentPlanPrice
                }}/year). This will extend your subscription by one year from
                your current expiration date.
              </div>
              <div class="early-renewal-modal__text2">
                <ul>
                  <li>
                    You'll be charged ${{ promotionContent.price_usd }} today,
                    and your new renewal date will be {{ newExpirationDate }}.
                  </li>
                  <li>
                    After this first year, future renewals will be billed at the
                    regular price of ${{ currentPlanPrice }}/year.
                  </li>
                </ul>
              </div>
              <div class="early-renewal-modal__text3">
                We'll charge your card ending in {{ lastFour }}.
                <router-link
                  :to="{
                    name: 'user-dashboard',
                    params: { mode: 'profile' },
                  }"
                >
                  Update billing information
                </router-link>
              </div>
            </div>
          </div>
          <div class="early-renewal-modal__ctas">
            <button class="btn btn--secondary" @click="$emit('close-modal')">
              Cancel
            </button>
            <button class="btn btn--primary" @click="addYearConfirm">
              Confirm renewal
            </button>
          </div>
        </div>
        <div v-else class="early-renewal-modal__content">
          <div class="early-renewal-modal__inner-content-loading">
            <ActionLoader />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { XIcon } from 'vue-feather-icons'
import { format } from 'date-fns'
import Plan from '@/components/modals/upgrade-plan-modal/Plan.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'

const recurlyKey = process.env.VUE_APP_RECURLY_KEY

export default {
  name: 'EarlyRenewalModal',
  components: {
    XIcon,
    Plan,
    ActionLoader,
  },
  props: {
    showClose: {
      type: Boolean,
      default: true,
    },
    currentPlan: {
      type: Object,
      default: () => ({}),
    },
    promotionContent: {
      type: Object,
      default: () => ({}),
    },
    paymentInformation: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    getSaving() {
      return this.currentPlanPrice - this.promotionContent.price_usd
    },
    currentPlanPrice() {
      return this.currentPlan.price_cents / 100
    },
    newExpirationDate() {
      const newDate = this.promotionContent.new_renews_at
      return format(new Date(newDate), 'MMMM do, yyyy')
    },
    lastFour() {
      return this.paymentInformation.last4_of_cc
    },
    paymentMethodId() {
      return this.paymentInformation.billing_account_code
    },
  },
  created() {
    const body = document.querySelector('body')
    body.classList.add('modal-open')
    window.recurly.configure(recurlyKey)
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
  methods: {
    addYearConfirm() {
      this.$emit('add-year-confirm')
    },
  },
}
</script>
<style lang="scss" scoped>
.early-renewal-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 220;
  overflow-y: scroll;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(17.5px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 65px;
  @include tablet {
    padding-top: 0;
    justify-content: center;
  }
  &__modal {
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 20px 20px 0 0;
    background: var(--darker-gray);
    @include tablet {
      width: 1140px;
      border-radius: 35px;
    }
  }
  &__header {
    position: relative;
    display: flex;
    padding: 10px;
    justify-content: center;
    align-items: center;
    align-self: stretch;
    border-radius: 20px 20px 0 0;
    background: var(--glass-mid-gray);
    color: var(--white);
    text-align: center;
    font-size: 16px;
    font-weight: 500;
    line-height: 23px; /* 143.75% */
    width: 100%;
    @include tablet {
      border-radius: 35px 35px 0px 0px;
      font-size: 24px;
      font-weight: 700;
      line-height: 32px;
      padding: 20px;
    }
    &-text {
      flex: 1 0 0;
    }
  }
  &__close {
    display: flex;
    margin: 0;
    @include tablet {
      position: absolute;
      right: 0;
      top: 0;
      margin: 30px;
    }
    background: none;
    border: none;
    cursor: pointer;
    &--icon {
      stroke: var(--white);
    }
  }
  &__content-wrapper {
    display: flex;
    width: 100%;
    padding: 30px;
    justify-content: center;
    align-items: flex-end;
    gap: 30px;
    @include tablet {
      width: 1140px;
      padding: 60px;
    }
  }
  &__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 32px;
    flex: 1 0 0;
    min-height: 600px;
  }
  &__inner-content {
    display: flex;
    align-items: flex-start;
    gap: 30px;
    align-self: stretch;
    flex-direction: column;
    color: var(--white);
    a {
      color: var(--purple-light);
    }
    &-loading {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 20px;
      width: 100%;
    }
    @include tablet {
      flex-direction: row;
    }
  }
  &__ctas {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 20px;
    align-self: stretch;
    position: sticky;
    bottom: 0;
    width: 100%;
    background: var(--darker-gray);
    display: flex;
    flex-wrap: wrap;
    flex-direction: column-reverse;
    button {
      width: 100%;
      @include tablet {
        width: auto;
      }
    }
    @include tablet {
      position: relative;
      bottom: auto;
      width: auto;
      background: transparent;
      display: flex;
      flex-wrap: nowrap;
      flex-direction: row;
    }
  }
  &__left-side {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    flex: 1 0 0;
    width: 100%;
    @include tablet {
      width: auto;
    }
  }
  &__right-side {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 30px;
    flex: 1 0 0;
    ul {
      padding-left: 20px;
      margin: 0;
    }
  }
  &__title {
    color: var(--dusty-gray);
    font-size: rem(20px);
    font-style: normal;
    font-weight: 700;
    line-height: rem(26px);
  }
  &__text1 {
    color: var(--white);
    font-size: rem(16px);
    font-style: normal;
    font-weight: 700;
    line-height: rem(24px);
  }
  &__text2 {
    color: var(--light-blue-gray);
    font-size: rem(16px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(24px);
  }
  &__text3 {
    color: var(--dusty-gray);
    font-size: rem(16px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(24px);
  }
  &__small-print {
    color: var(--light-blue-gray);
    font-size: rem(12px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(14px);
  }
}
</style>
